import { Link } from "./LocaleLink";
import { Newspaper, ArrowUpRight, Mic, FileText } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "../context/LanguageContext";

/**
 * Compact "Press & Expert Commentary" strip.
 *
 * Rather than fabricate "As seen in Forbes/Bloomberg" logos we don't own, this
 * strip is engineered to *earn* backlinks: it clearly signals to journalists,
 * researchers and AI crawlers that our team is available for expert commentary
 * on the French Riviera property market, and points them to the on-site
 * Press & Media Kit (/press) where they can grab facts, quotes and logos.
 */
export const PressStrip = () => {
  const { t } = useLang();
  const topics = t("press.strip.topics");
  return (
    <section
      data-testid="press-strip"
      aria-labelledby="press-strip-title"
      className="border-y border-border/60 bg-background/60"
    >
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary flex items-center gap-2">
                <Newspaper size={14} strokeWidth={1.6} />
                {t("press.strip.overline")}
              </p>
              <h2
                id="press-strip-title"
                className="mt-4 font-headings text-2xl md:text-3xl leading-tight text-foreground"
              >
                {t("press.strip.title")}
              </h2>
              <p className="mt-4 font-body font-light text-foreground/70 leading-relaxed max-w-md">
                {t("press.strip.body")}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/press"
                  data-testid="press-strip-mediakit"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 uppercase tracking-[0.2em] text-[11px] font-body transition-all"
                >
                  <FileText size={13} /> {t("press.strip.ctaKit")}
                </Link>
                <a
                  href="mailto:press@velmiraliving.com?subject=Press%20enquiry"
                  data-testid="press-strip-email"
                  className="inline-flex items-center gap-2 border border-foreground/30 hover:border-primary hover:text-primary px-6 py-3 uppercase tracking-[0.2em] text-[11px] font-body transition-all"
                >
                  <Mic size={13} /> {t("press.strip.ctaEmail")}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-4">
                {t("press.strip.topicsLabel")}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {topics.map((topic, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 border-l border-primary/40 pl-4 py-1 font-body font-light text-sm text-foreground/85 leading-snug"
                    data-testid={`press-strip-topic-${i}`}
                  >
                    <span>{topic}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/riviera-real-estate-data-2026"
                data-testid="press-strip-data-report"
                className="mt-8 inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.25em] text-primary hover:text-primary/80 transition-colors"
              >
                {t("press.strip.dataCta")} <ArrowUpRight size={13} />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PressStrip;
